import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import KlausurErstellerOrderingModal from "@/features/klausur/components/klausurErstellerOrderingModal.component";
import type { OrderingItem } from "@/features/klausur/components/klausurErstellerOrderingModal.component";
import { useAuth } from "@/lib/contexts/auth.context";
import { KlausurService } from "@/lib/klausur/klausur.service";
import { useKlausurEditing } from "@/features/klausur/context/klausurEditingProvider";

interface OpenOrderingModalOptions {
	title: string;
	description?: string;
	items: OrderingItem[];
	onConfirm: (items: OrderingItem[]) => Promise<void> | void;
}

interface OrderingModalContextValue {
	openOrderingModal: (opts: OpenOrderingModalOptions) => void;
	openKorrekturbogenItemsOrdering: (items: OrderingItem[]) => void;
	openAufgabenstellungenOrdering: (items: OrderingItem[]) => void;
}

/**
 * Context for the OrderingModalProvider
 */
const OrderingModalContext = createContext<OrderingModalContextValue | null>(
	null,
);

/**
 * Provider for the OrderingModalContext
 * - used to open the ordering modal for different types of items
 */
export function OrderingModalProvider(props: { children: React.ReactNode }) {
	const { currentOrganization } = useAuth();
	const { klausurId, reloadKlausur } = useKlausurEditing();
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [modalTitle, setModalTitle] = useState<string>("");
	const [modalDescription, setModalDescription] = useState<string | undefined>(
		undefined,
	);
	const [items, setItems] = useState<OrderingItem[]>([]);
	const [confirmHandler, setConfirmHandler] = useState<
		((items: OrderingItem[]) => Promise<void> | void) | null
	>(null);

	/**
	 * Open the ordering modal for any type of items
	 * @param opts - The options for the modal
	 */
	const openOrderingModal = useCallback((opts: OpenOrderingModalOptions) => {
		setModalTitle(opts.title);
		setModalDescription(opts.description);
		setItems(opts.items);
		setConfirmHandler(() => opts.onConfirm);
		setIsOpen(true);
	}, []);

	/**
	 * Open the ordering modal for korrekturbogen items
	 * @param items - The items to order
	 */
	const openKorrekturbogenItemsOrdering = useCallback(
		(items: OrderingItem[]) => {
			openOrderingModal({
				title: "Reihenfolge der Korrekturbogen-Elemente",
				description:
					"Ziehen Sie die Elemente, um die Reihenfolge der Korrekturbogen-Elemente zu ändern.",
				items,
				onConfirm: async (reordered) => {
					if (!currentOrganization?.organizationId || !klausurId) return;
					setIsLoading(true);
					try {
						await KlausurService.getInstance().bulkUpdateKorrekturbogenItemsIndexes(
							currentOrganization.organizationId,
							klausurId,
							reordered.map((r) => ({ id: r.id, index: r.index })),
						);
						await reloadKlausur();
						setIsOpen(false);
					} finally {
						setIsLoading(false);
					}
				},
			});
		},
		[
			currentOrganization?.organizationId,
			klausurId,
			openOrderingModal,
			reloadKlausur,
		],
	);

	/**
	 * Open the ordering modal for aufgabenstellungen
	 */
	const openAufgabenstellungenOrdering = useCallback(
		(items: OrderingItem[]) => {
			openOrderingModal({
				title: "Reihenfolge der Aufgabenstellungen",
				description:
					"Ziehen Sie die Elemente, um die Reihenfolge der Aufgabenstellungen zu ändern.",
				items,
				onConfirm: async (reordered) => {
					if (!currentOrganization?.organizationId || !klausurId) return;
					setIsLoading(true);
					try {
						await KlausurService.getInstance().bulkUpdateAufgabenstellungenIndexes(
							currentOrganization.organizationId,
							klausurId,
							reordered.map((r) => ({ id: r.id, index: r.index })),
						);
						await reloadKlausur();
						setIsOpen(false);
					} finally {
						setIsLoading(false);
					}
				},
			});
		},
		[
			currentOrganization?.organizationId,
			klausurId,
			openOrderingModal,
			reloadKlausur,
		],
	);

	/**
	 * Value for the ordering modal context
	 */
	const value = useMemo(
		() => ({
			openOrderingModal,
			openKorrekturbogenItemsOrdering,
			openAufgabenstellungenOrdering,
		}),
		[
			openOrderingModal,
			openKorrekturbogenItemsOrdering,
			openAufgabenstellungenOrdering,
		],
	);

	return (
		<OrderingModalContext.Provider value={value}>
			{props.children}
			<KlausurErstellerOrderingModal
				open={isOpen}
				title={modalTitle}
				description={modalDescription}
				items={items}
				loading={isLoading}
				onClose={() => setIsOpen(false)}
				onConfirm={async (newItems) => {
					if (!confirmHandler) return;
					await Promise.resolve(confirmHandler(newItems));
				}}
			/>
		</OrderingModalContext.Provider>
	);
}

export function useOrderingModal() {
	const ctx = useContext(OrderingModalContext);
	if (!ctx)
		throw new Error(
			"useOrderingModal must be used within OrderingModalProvider",
		);
	return ctx;
}
